import { DateRange } from '../../constants/ui';
import { MINUTE_IN_MS, HOUR_IN_MS, isDailyAggregationRange } from './constants';
import type { TimezoneMode } from '../../utils/storage';

/**
 * Safari互換の日付パース関数（UTC解釈）
 * Parses "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD" format into a UTC timestamp.
 * API data is always UTC, so we parse as UTC epoch ms.
 */
export const safeParseDate = (dateStr: string): number => {
  const [datePart, timePart = '00:00:00'] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return Date.UTC(year, month - 1, day, hour, minute, second);
};

export const formatDayKey = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDayStartTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
};

// ── TZ-aware display helpers ─────────────────────────
export const getHoursInTZ = (ts: number, tz: TimezoneMode): number =>
  tz === 'utc' ? new Date(ts).getUTCHours() : new Date(ts).getHours();

export const getMinutesInTZ = (ts: number, tz: TimezoneMode): number =>
  tz === 'utc' ? new Date(ts).getUTCMinutes() : new Date(ts).getMinutes();

export const getMonthInTZ = (ts: number, tz: TimezoneMode): number =>
  tz === 'utc' ? new Date(ts).getUTCMonth() + 1 : new Date(ts).getMonth() + 1;

export const getDateInTZ = (ts: number, tz: TimezoneMode): number =>
  tz === 'utc' ? new Date(ts).getUTCDate() : new Date(ts).getDate();

export const getFullYearInTZ = (ts: number, tz: TimezoneMode): number =>
  tz === 'utc' ? new Date(ts).getUTCFullYear() : new Date(ts).getFullYear();

/** Format a UTC timestamp into "YYYY-MM-DD HH:mm:ss" in the selected TZ */
export const formatTimestampForDisplay = (ts: number, tz: TimezoneMode): string => {
  const y = getFullYearInTZ(ts, tz);
  const mo = getMonthInTZ(ts, tz).toString().padStart(2, '0');
  const d = getDateInTZ(ts, tz).toString().padStart(2, '0');
  const h = getHoursInTZ(ts, tz).toString().padStart(2, '0');
  const mi = getMinutesInTZ(ts, tz).toString().padStart(2, '0');
  const s = tz === 'utc'
    ? new Date(ts).getUTCSeconds().toString().padStart(2, '0')
    : new Date(ts).getSeconds().toString().padStart(2, '0');
  return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
};

export const generateTimeTicks = (timestamps: number[], range: DateRange): number[] => {
  const safeValues = timestamps
    .filter((value): value is number => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (safeValues.length === 0) {
    return [];
  }

  const start = safeValues[0];
  const end = safeValues[safeValues.length - 1];

  if (start === end) {
    return [start];
  }

  const span = end - start;
  let step: number;

  switch (range) {
    case '1hour':
      step = 10 * MINUTE_IN_MS;
      break;
    case '3hours':
      step = 30 * MINUTE_IN_MS;
      break;
    case '6hours':
      step = HOUR_IN_MS;
      break;
    case '1day':
    case 'yesterday':
      step = HOUR_IN_MS;
      break;
    case '7days':
      step = 24 * HOUR_IN_MS;
      break;
    case '30days':
      step = 2 * 24 * HOUR_IN_MS;
      break;
    case '60days':
      step = 4 * 24 * HOUR_IN_MS;
      break;
    case '90days':
      step = 7 * 24 * HOUR_IN_MS;
      break;
    case '120days':
      step = 10 * 24 * HOUR_IN_MS;
      break;
    case '150days':
      step = 14 * 24 * HOUR_IN_MS;
      break;
    case '180days':
      step = 14 * 24 * HOUR_IN_MS;
      break;
    case '360days':
      step = 30 * 24 * HOUR_IN_MS;
      break;
    default: {
      const approxSegments = Math.max(1, Math.round(span / (HOUR_IN_MS * 6)));
      step = approxSegments * HOUR_IN_MS;
      break;
    }
  }

  if (!Number.isFinite(step) || step <= 0) {
    step = Math.max(Math.round(span / 6), MINUTE_IN_MS);
  }

  const ticks: number[] = [];
  const baseline = Math.floor(start / step) * step;

  for (let current = baseline; current <= end; current += step) {
    if (current >= start && current <= end) {
      ticks.push(current);
    }
  }

  ticks.push(start, end);

  const uniqueSorted = Array.from(new Set(ticks)).sort((a, b) => a - b);

  if (isDailyAggregationRange(range)) {
    return uniqueSorted;
  }

  if (uniqueSorted.length > 12) {
    const stride = Math.ceil(uniqueSorted.length / 12);
    return uniqueSorted.filter((_, index) => index % stride === 0 || index === uniqueSorted.length - 1);
  }

  return uniqueSorted;
};
